import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  adminGetPartnerInvoice,
  adminCreatePartnerCreditNote,
  adminUpdatePartnerInvoice,
  adminDeletePartnerInvoice,
  adminFetchPartnerInvoiceCandidates,
} from "../core/axios/endpoints";
import { useBodyScrollLock } from "../core/scroll-lock";
import ConfirmModal from "./confirm-modal";
import { lineModuleLabel } from "../core/partner-subsidie";

const fmtEUR = (n) =>
  `€ ${Number(n ?? 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("nl-NL") : "-");
// Lokale datum (zelfde dag als fmtDate toont), niet via UTC: anders schuift
// een factuurdatum met tijdstip na 22:00 een dag terug bij bewerken.
const toInputDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const STATUS_LABELS = {
  draft: "Concept",
  sent: "Verstuurd",
  paid: "Betaald",
  overdue: "Achterstallig",
  cancelled: "Geannuleerd",
};

// Zelfde afronding als de server (computeLineTotals), alleen voor weergave.
const lineTotals = (amountExcl, vatRate = 0.21) => {
  const excl = Math.round((Number(amountExcl) || 0) * 100) / 100;
  const vat = Math.round(excl * vatRate * 100) / 100;
  const incl = Math.round((excl + vat) * 100) / 100;
  return { amountExcl: excl, vatAmount: vat, amountIncl: incl };
};

const sumLines = (lines) =>
  lines.reduce(
    (acc, l) => {
      const t = lineTotals(l.amountExcl, l.vatRate);
      acc.subtotalExcl += t.amountExcl;
      acc.vatAmount += t.vatAmount;
      acc.totalIncl += t.amountIncl;
      return acc;
    },
    { subtotalExcl: 0, vatAmount: 0, totalIncl: 0 },
  );

const draftFromInvoice = (inv) => ({
  kenmerk: inv.kenmerk ?? "",
  invoiceDate: toInputDate(inv.invoiceDate),
  dueDate: toInputDate(inv.dueDate),
  billingEmail: inv.billingEmail ?? "",
  notes: inv.notes ?? "",
  lines: (inv.lines ?? []).map((l) => ({
    btwApplicationId: String(l.btwApplicationId),
    applicationModel: l.applicationModel ?? "BtwApplications",
    module: l.module,
    moduleLabel: l.moduleLabel,
    customerName: l.customerName,
    applicationDate: l.applicationDate,
    description: l.description ?? "",
    amountExcl: String(l.amountExcl ?? 0),
    vatRate: l.vatRate ?? 0.21,
  })),
});

const inputCls =
  "mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink placeholder:text-ink-subtlest focus:border-brand focus:outline-none disabled:bg-sunken disabled:opacity-50";

const PartnerInvoiceDetailModal = ({
  isOpen,
  invoiceId,
  onClose,
  onChanged,
  initialEdit = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [selectedLines, setSelectedLines] = useState(new Set());
  const [creditReason, setCreditReason] = useState("");
  const [confirmCreditOpen, setConfirmCreditOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Bewerk-modus
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState("");
  // null = picker dicht, -1 = toevoegen, >=0 = regel op die index vervangen
  const [pickerFor, setPickerFor] = useState(null);

  useBodyScrollLock(isOpen);

  const load = useCallback(() => {
    if (!invoiceId) return () => {};
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminGetPartnerInvoice(invoiceId)
      .then((res) => {
        if (cancelled) return;
        const inv = res?.data ?? res;
        setInvoice(inv);
        setSelectedLines(new Set());
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e?.response?.data?.error ?? e?.message ?? "Kon factuur niet laden.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [invoiceId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    return load();
  }, [isOpen, load]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setPickerFor(null);
    setSaveError(null);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape" || submitting) return;
      // Een open ConfirmModal vangt ESC zelf af; anders sluit één toets
      // de bevestiging én deze modal tegelijk.
      if (confirmCreditOpen || confirmDeleteOpen) return;
      if (pickerFor !== null) setPickerFor(null);
      else if (editing) cancelEdit();
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    isOpen,
    submitting,
    onClose,
    editing,
    pickerFor,
    confirmCreditOpen,
    confirmDeleteOpen,
    cancelEdit,
  ]);

  const isCredit = invoice?.type === "credit_note";
  const canEdit =
    !!invoice &&
    !isCredit &&
    invoice.status !== "cancelled" &&
    !(invoice.creditedAmount > 0);
  const canDelete =
    canEdit && invoice.status !== "paid";
  const canCredit =
    !!invoice &&
    !isCredit &&
    invoice.status !== "draft" &&
    invoice.status !== "cancelled";

  const startEdit = useCallback(() => {
    if (!invoice) return;
    setDraft(draftFromInvoice(invoice));
    setSaveError(null);
    setPickerFor(null);
    setEditing(true);
  }, [invoice]);

  // Direct in bewerk-modus openen als de lijstpagina dat vraagt. Eén keer per
  // factuur, anders springt Annuleren meteen weer terug in bewerk-modus.
  const initialEditApplied = useRef(false);
  useEffect(() => {
    initialEditApplied.current = false;
  }, [invoiceId]);
  useEffect(() => {
    if (!initialEdit || initialEditApplied.current) return;
    if (!invoice || !canEdit || loading) return;
    initialEditApplied.current = true;
    startEdit();
  }, [initialEdit, invoice, canEdit, loading, startEdit]);

  const loadCandidates = useCallback(async () => {
    if (!invoice) return;
    setCandidatesLoading(true);
    try {
      const res = await adminFetchPartnerInvoiceCandidates(invoice._id);
      const payload = res?.data ?? res;
      setCandidates(payload?.lines ?? []);
    } catch (e) {
      setSaveError(
        e?.response?.data?.error ?? "Kon beschikbare aanvragen niet laden.",
      );
      setCandidates(null);
      setPickerFor(null);
    } finally {
      setCandidatesLoading(false);
    }
  }, [invoice]);

  const openPicker = (index) => {
    setCandidateSearch("");
    setPickerFor(index);
    if (candidates === null) loadCandidates();
  };

  const pickCandidate = (cand) => {
    setDraft((d) => {
      const lines = [...d.lines];
      const newLine = {
        btwApplicationId: String(cand.btwApplicationId),
        applicationModel: cand.applicationModel ?? "BtwApplications",
        module: cand.module,
        moduleLabel: cand.moduleLabel,
        customerName: cand.customerName,
        applicationDate: cand.applicationDate,
        description: cand.description ?? "",
        amountExcl: String(cand.amountExcl ?? 0),
        vatRate: cand.vatRate ?? 0.21,
      };
      if (pickerFor >= 0 && pickerFor < lines.length) {
        // Vervangen: bedrag van de oude regel houden, plek behouden.
        newLine.amountExcl = lines[pickerFor].amountExcl;
        lines[pickerFor] = newLine;
      } else {
        lines.push(newLine);
      }
      return { ...d, lines };
    });
    setPickerFor(null);
  };

  const updateLine = (index, patch) =>
    setDraft((d) => {
      const lines = d.lines.map((l, i) => (i === index ? { ...l, ...patch } : l));
      return { ...d, lines };
    });

  const removeLine = (index) => {
    // Picker wijst op index; na weghalen zou die naar een andere regel wijzen.
    setPickerFor(null);
    setDraft((d) => ({ ...d, lines: d.lines.filter((_, i) => i !== index) }));
  };

  const draftTotals = useMemo(
    () => (draft ? sumLines(draft.lines) : null),
    [draft],
  );
  const draftAppIds = useMemo(
    () => new Set((draft?.lines ?? []).map((l) => l.btwApplicationId)),
    [draft],
  );
  const filteredCandidates = useMemo(() => {
    if (!candidates) return [];
    const q = candidateSearch.trim().toLowerCase();
    return candidates.filter(
      (c) =>
        !draftAppIds.has(String(c.btwApplicationId)) &&
        (!q || (c.customerName ?? "").toLowerCase().includes(q)),
    );
  }, [candidates, candidateSearch, draftAppIds]);

  const saveEdit = async () => {
    if (!invoice || !draft) return;
    if (draft.lines.length === 0) {
      setSaveError(
        "Een factuur heeft minstens één regel. Verwijder anders de hele factuur.",
      );
      return;
    }
    if (!draft.kenmerk.trim()) {
      setSaveError("Kenmerk mag niet leeg zijn.");
      return;
    }
    if (!draft.billingEmail.trim()) {
      setSaveError("E-mail mag niet leeg zijn.");
      return;
    }
    // Een number-input geeft "" terug bij gewist of ongeldig: niet stil € 0 maken.
    const badLine = draft.lines.find((l) => {
      const raw = String(l.amountExcl).trim().replace(",", ".");
      const n = Number(raw);
      return raw === "" || !Number.isFinite(n) || n < 0;
    });
    if (badLine) {
      setSaveError(`Vul een geldig bedrag in bij ${badLine.customerName}.`);
      return;
    }
    setSubmitting(true);
    setSaveError(null);
    try {
      const res = await adminUpdatePartnerInvoice(invoice._id, {
        kenmerk: draft.kenmerk,
        invoiceDate: draft.invoiceDate || undefined,
        dueDate: draft.dueDate ? draft.dueDate : null,
        billingEmail: draft.billingEmail,
        notes: draft.notes,
        lines: draft.lines.map((l) => ({
          btwApplicationId: l.btwApplicationId,
          applicationModel: l.applicationModel ?? "BtwApplications",
          amountExcl: Number(String(l.amountExcl).replace(",", ".")),
          vatRate: l.vatRate,
          description: l.description,
        })),
      });
      const saved = res?.data ?? res;
      if (saved && saved._id) {
        setInvoice(saved);
        setSelectedLines(new Set());
      } else {
        load();
      }
      cancelEdit();
      setCandidates(null);
      onChanged?.();
    } catch (e) {
      setSaveError(e?.response?.data?.error ?? "Opslaan mislukt.");
    } finally {
      setSubmitting(false);
    }
  };

  const runDelete = async () => {
    if (!invoice) return;
    setSubmitting(true);
    try {
      await adminDeletePartnerInvoice(invoice._id);
      setConfirmDeleteOpen(false);
      onChanged?.();
      onClose();
    } catch (e) {
      setConfirmDeleteOpen(false);
      setSaveError(e?.response?.data?.error ?? "Verwijderen mislukt.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleLine = (appId) => {
    setSelectedLines((prev) => {
      const next = new Set(prev);
      const key = String(appId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const runCreditNote = async () => {
    if (!invoice) return;
    const applicationIds =
      selectedLines.size > 0 ? Array.from(selectedLines) : undefined;
    setSubmitting(true);
    try {
      await adminCreatePartnerCreditNote(invoice._id, {
        applicationIds,
        reason: creditReason || undefined,
      });
      setConfirmCreditOpen(false);
      setCreditReason("");
      onChanged?.();
      onClose();
    } catch (e) {
      setConfirmCreditOpen(false);
      setSaveError(e?.response?.data?.error ?? "Creditnota aanmaken mislukt.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  const isPaid = invoice?.status === "paid";
  const totalChangedWhilePaid =
    isPaid &&
    draftTotals &&
    Math.abs(draftTotals.totalIncl - (invoice.totalIncl ?? 0)) >= 0.005;

  const renderViewLines = () => (
    <div className="mt-3 divide-y divide-line rounded border border-line">
      {(invoice.lines ?? []).map((line) => {
        const key = String(line.btwApplicationId);
        const checked = selectedLines.has(key);
        return (
          <label
            key={key}
            className={`flex items-center gap-3 p-3 ${
              canCredit ? "cursor-pointer hover:bg-sunken" : ""
            }`}
          >
            {canCredit ? (
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleLine(key)}
                className="h-4 w-4 rounded border-line text-brand focus:ring-0"
              />
            ) : null}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-ink">
                {line.customerName}
                <span className="ml-2 rounded bg-[#091E420F] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-ink-subtle">
                  {lineModuleLabel(line)}
                </span>
              </p>
              <p className="text-xs text-ink-subtlest">
                {line.description} &middot; {fmtDate(line.applicationDate)}
              </p>
            </div>
            <div className="text-right tabular-nums">
              <div className="text-sm font-medium">{fmtEUR(line.amountIncl)}</div>
              <div className="text-xs text-ink-subtlest">
                excl. {fmtEUR(line.amountExcl)}
              </div>
            </div>
          </label>
        );
      })}
    </div>
  );

  const renderEditLines = () => (
    <div className="mt-3 divide-y divide-line rounded border border-line">
      {draft.lines.length === 0 ? (
        <p className="p-4 text-center text-sm text-ink-subtlest">
          Geen regels meer. Voeg een aanvraag toe of verwijder de factuur.
        </p>
      ) : null}
      {draft.lines.map((line, index) => {
        const t = lineTotals(
          String(line.amountExcl).replace(",", "."),
          line.vatRate,
        );
        return (
          <div key={line.btwApplicationId} className="p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-ink">
                  {line.customerName}
                  <span className="ml-2 rounded bg-[#091E420F] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-ink-subtle">
                    {lineModuleLabel(line)}
                  </span>
                </p>
                <p className="text-xs text-ink-subtlest">
                  Aanvraag van {fmtDate(line.applicationDate)}
                </p>
              </div>
              <div className="flex shrink-0 gap-1.5">
                <button
                  type="button"
                  onClick={() => openPicker(index)}
                  disabled={submitting}
                  className="h-7 rounded bg-[#091E420F] px-2 text-xs font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
                  title="Vervang deze regel door een andere aanvraag (zelfde bedrag)"
                >
                  Vervang
                </button>
                <button
                  type="button"
                  onClick={() => removeLine(index)}
                  disabled={submitting}
                  className="h-7 rounded px-2 text-xs font-medium text-[#AE2A19] hover:bg-[#FFECEB] disabled:cursor-not-allowed disabled:opacity-50"
                  title="Regel weghalen; de aanvraag komt terug op te factureren"
                >
                  Weghalen
                </button>
              </div>
            </div>
            <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_9rem]">
              <label className="block text-xs font-semibold text-ink-subtlest">
                Omschrijving
                <input
                  type="text"
                  value={line.description}
                  onChange={(e) => updateLine(index, { description: e.target.value })}
                  disabled={submitting}
                  className={inputCls}
                />
              </label>
              <label className="block text-xs font-semibold text-ink-subtlest">
                Bedrag excl. btw
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={line.amountExcl}
                  onChange={(e) => updateLine(index, { amountExcl: e.target.value })}
                  disabled={submitting}
                  className={`${inputCls} text-right tabular-nums`}
                />
                <span className="mt-0.5 block text-right text-[11px] text-ink-subtlest tabular-nums">
                  incl. {fmtEUR(t.amountIncl)}
                </span>
              </label>
            </div>
          </div>
        );
      })}
      <div className="p-3">
        <button
          type="button"
          onClick={() => openPicker(-1)}
          disabled={submitting}
          className="h-8 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
        >
          + Aanvraag toevoegen
        </button>
      </div>
    </div>
  );

  const renderPicker = () => (
    <div className="mt-3 rounded bg-brand-subtle p-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-ink">
          {pickerFor >= 0
            ? `Vervang ${draft.lines[pickerFor]?.customerName ?? "regel"} door`
            : "Aanvraag toevoegen"}
        </p>
        <button
          type="button"
          onClick={() => setPickerFor(null)}
          className="text-xs font-medium text-brand hover:underline"
        >
          Sluiten
        </button>
      </div>
      <input
        type="search"
        value={candidateSearch}
        onChange={(e) => setCandidateSearch(e.target.value)}
        placeholder="Zoek op naam"
        className={`${inputCls} bg-white`}
        autoFocus
      />
      <div className="mt-2 max-h-56 divide-y divide-line overflow-y-auto rounded bg-white shadow-card">
        {candidatesLoading ? (
          <p className="p-3 text-sm text-ink-subtlest">Laden…</p>
        ) : filteredCandidates.length === 0 ? (
          <p className="p-3 text-sm text-ink-subtlest">
            Geen aanvragen van deze partner die nog te factureren zijn.
          </p>
        ) : (
          filteredCandidates.map((c) => (
            <button
              key={String(c.btwApplicationId)}
              type="button"
              onClick={() => pickCandidate(c)}
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-[#091E420F]"
            >
              <span className="min-w-0">
                <span className="block truncate font-medium text-ink">
                  {c.customerName}
                  <span className="ml-2 rounded bg-[#091E420F] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-ink-subtle">
                    {lineModuleLabel(c)}
                  </span>
                  {c.isArchived ? (
                    <span className="ml-2 rounded bg-[#091E420F] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-ink-subtle">
                      archief
                    </span>
                  ) : null}
                </span>
                <span className="block text-xs text-ink-subtlest">
                  Aanvraag van {fmtDate(c.applicationDate)}
                </span>
              </span>
              <span className="shrink-0 tabular-nums text-ink">
                {fmtEUR(c.amountExcl)} excl.
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto bg-[#091E428A] p-4">
      <div className="my-8 w-full max-w-3xl rounded-md bg-white shadow-overlay">
        <div className="flex items-start justify-between gap-4 px-6 pt-6">
          <div>
            <h2 className="text-xl font-medium text-ink">
              {invoice?.invoiceNumber ?? "Factuur"}
              {editing ? (
                <span className="ml-2 inline-flex items-center rounded bg-[#FFF7D6] px-1.5 py-px align-middle text-[11px] font-bold uppercase tracking-wide text-[#7F5F01]">
                  Bewerken
                </span>
              ) : null}
            </h2>
            <p className="mt-1 text-sm text-ink-subtlest">
              {invoice?.billingName} &middot; {fmtDate(invoice?.invoiceDate)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-ink-subtlest hover:bg-[#091E420F] disabled:opacity-50"
            aria-label="Sluiten"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 11-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 11-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-4 text-sm text-ink">
          {loading ? (
            <div className="py-12 text-center text-sm text-ink-subtlest">Laden…</div>
          ) : error ? (
            <div className="rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">
              {error}
            </div>
          ) : invoice ? (
            <>
              {saveError ? (
                <div className="mb-4 rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">
                  {saveError}
                </div>
              ) : null}

              {editing && draft ? (
                <div className="grid gap-3 text-sm sm:grid-cols-2">
                  <label className="block text-xs font-semibold text-ink-subtlest">
                    Kenmerk
                    <input
                      type="text"
                      value={draft.kenmerk}
                      onChange={(e) => setDraft({ ...draft, kenmerk: e.target.value })}
                      disabled={submitting}
                      className={inputCls}
                    />
                  </label>
                  <label className="block text-xs font-semibold text-ink-subtlest">
                    E-mail
                    <input
                      type="email"
                      value={draft.billingEmail}
                      onChange={(e) =>
                        setDraft({ ...draft, billingEmail: e.target.value })
                      }
                      disabled={submitting}
                      className={inputCls}
                    />
                  </label>
                  <label className="block text-xs font-semibold text-ink-subtlest">
                    Factuurdatum
                    <input
                      type="date"
                      value={draft.invoiceDate}
                      onChange={(e) =>
                        setDraft({ ...draft, invoiceDate: e.target.value })
                      }
                      disabled={submitting}
                      className={inputCls}
                    />
                  </label>
                  <label className="block text-xs font-semibold text-ink-subtlest">
                    Vervaldatum
                    <input
                      type="date"
                      value={draft.dueDate}
                      onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })}
                      disabled={submitting}
                      className={inputCls}
                    />
                  </label>
                  <label className="block text-xs font-semibold text-ink-subtlest sm:col-span-2">
                    Notities
                    <textarea
                      rows={2}
                      value={draft.notes}
                      onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                      disabled={submitting}
                      className={inputCls}
                    />
                  </label>
                </div>
              ) : (
                <div className="grid gap-3 text-sm sm:grid-cols-2">
                  <div>
                    <span className="text-ink-subtlest">Kenmerk:</span>{" "}
                    <span className="font-medium">{invoice.kenmerk}</span>
                  </div>
                  <div>
                    <span className="text-ink-subtlest">Status:</span>{" "}
                    {STATUS_LABELS[invoice.status] ?? invoice.status}
                  </div>
                  <div>
                    <span className="text-ink-subtlest">Email:</span>{" "}
                    {invoice.billingEmail}
                  </div>
                  {invoice.dueDate ? (
                    <div>
                      <span className="text-ink-subtlest">Vervaldatum:</span>{" "}
                      {fmtDate(invoice.dueDate)}
                    </div>
                  ) : null}
                  {invoice.sentAt ? (
                    <div>
                      <span className="text-ink-subtlest">Verstuurd op:</span>{" "}
                      {fmtDate(invoice.sentAt)}
                    </div>
                  ) : null}
                  {invoice.paidAt ? (
                    <div>
                      <span className="text-ink-subtlest">Betaald op:</span>{" "}
                      {fmtDate(invoice.paidAt)}
                    </div>
                  ) : null}
                  {invoice.creditedAmount ? (
                    <div className="text-[#AE2A19]">
                      <span className="text-ink-subtlest">Gecrediteerd:</span>{" "}
                      {fmtEUR(invoice.creditedAmount)}
                    </div>
                  ) : null}
                </div>
              )}

              {/* Regels */}
              <div className="mt-6">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
                  Regels ({(editing ? draft?.lines : invoice.lines)?.length ?? 0})
                </h3>
                {editing && draft ? renderEditLines() : renderViewLines()}
                {editing && pickerFor !== null ? renderPicker() : null}
              </div>

              {editing && isPaid ? (
                <div
                  className={`mt-4 rounded px-3 py-2 text-sm ${
                    totalChangedWhilePaid
                      ? "bg-[#FFF7D6] text-[#7F5F01]"
                      : "bg-sunken text-ink-subtle"
                  }`}
                >
                  Deze factuur staat op betaald. Aanvragen die je weghaalt komen
                  terug op te factureren; aanvragen die je toevoegt erven de
                  betaalstatus.
                  {totalChangedWhilePaid ? (
                    <>
                      {" "}
                      Let op: het totaal wijzigt van {fmtEUR(invoice.totalIncl)}{" "}
                      naar {fmtEUR(draftTotals.totalIncl)}, terwijl de betaling
                      al binnen is.
                    </>
                  ) : null}
                </div>
              ) : null}

              {/* Totals */}
              <div className="mt-4 flex justify-end">
                <div className="w-72 rounded bg-sunken p-3 text-sm text-ink">
                  {(() => {
                    const t = editing && draftTotals ? draftTotals : invoice;
                    return (
                      <>
                        <div className="flex justify-between py-0.5">
                          <span className="text-ink-subtlest">Subtotaal (excl.)</span>
                          <span className="tabular-nums">{fmtEUR(t.subtotalExcl)}</span>
                        </div>
                        <div className="flex justify-between py-0.5">
                          <span className="text-ink-subtlest">BTW</span>
                          <span className="tabular-nums">{fmtEUR(t.vatAmount)}</span>
                        </div>
                        <div className="mt-1 flex justify-between border-t border-line pt-1 font-semibold text-ink">
                          <span>Totaal (incl.)</span>
                          <span className="tabular-nums">{fmtEUR(t.totalIncl)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {!editing && invoice.notes ? (
                <p className="mt-4 rounded bg-sunken p-3 text-sm text-ink-subtle">
                  {invoice.notes}
                </p>
              ) : null}

              {!editing && canCredit ? (
                <div className="mt-6 rounded bg-[#FFECEB] p-4 text-[#AE2A19]">
                  <h4 className="text-sm font-semibold">
                    Creditnota maken
                  </h4>
                  <p className="mt-1 text-xs">
                    Alleen nodig als de partner deze factuur al ontvangen of betaald
                    heeft en er een officiële correctie moet komen. Voor een interne
                    correctie gebruik je Bewerken.
                  </p>
                  <p className="mt-1 text-xs">
                    {selectedLines.size === 0
                      ? "Geen regels geselecteerd: hele factuur wordt gecrediteerd."
                      : `${selectedLines.size} regel(s) geselecteerd voor creditering.`}
                  </p>
                  <textarea
                    rows={2}
                    value={creditReason}
                    onChange={(e) => setCreditReason(e.target.value)}
                    placeholder="Reden (optioneel)"
                    className="mt-2 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink placeholder:text-ink-subtlest focus:border-brand focus:outline-none"
                  />
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setConfirmCreditOpen(true)}
                      disabled={submitting}
                      className="h-8 rounded bg-[#C9372C] px-3 text-sm font-medium text-white hover:bg-[#AE2A19] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Maak creditnota
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 px-6 pb-6 pt-2">
          <div>
            {!editing && canDelete ? (
              <button
                type="button"
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={submitting}
                className="h-8 rounded px-3 text-sm font-medium text-[#AE2A19] hover:bg-[#FFECEB] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Factuur verwijderen
              </button>
            ) : null}
          </div>
          <div className="flex gap-2">
            {editing ? (
              <>
                <button
                  type="button"
                  onClick={cancelEdit}
                  disabled={submitting}
                  className="h-8 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Annuleren
                </button>
                <button
                  type="button"
                  onClick={saveEdit}
                  disabled={submitting}
                  className="h-8 rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? "Opslaan…" : "Opslaan"}
                </button>
              </>
            ) : (
              <>
                {canEdit ? (
                  <button
                    type="button"
                    onClick={startEdit}
                    disabled={submitting || loading}
                    className="h-8 rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Bewerken
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="h-8 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Sluiten
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmCreditOpen}
        variant="danger"
        title="Creditnota aanmaken?"
        message={
          selectedLines.size === 0
            ? "De gehele factuur wordt gecrediteerd. De creditnota wordt niet automatisch gemaild."
            : `${selectedLines.size} regel(s) worden gecrediteerd. De creditnota wordt niet automatisch gemaild.`
        }
        confirmLabel="Maak creditnota"
        cancelLabel="Annuleren"
        zIndex={10001}
        busy={submitting}
        onConfirm={runCreditNote}
        onCancel={() => setConfirmCreditOpen(false)}
      />

      <ConfirmModal
        isOpen={confirmDeleteOpen}
        variant="danger"
        title="Factuur verwijderen?"
        message={`Factuur ${invoice?.invoiceNumber ?? ""} wordt verwijderd en de gekoppelde aanvragen komen terug op te factureren. Het factuurnummer wordt niet opnieuw uitgegeven.`}
        confirmLabel="Verwijder factuur"
        cancelLabel="Annuleren"
        zIndex={10001}
        busy={submitting}
        onConfirm={runDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
    </div>,
    document.body,
  );
};

export default PartnerInvoiceDetailModal;
